import "./Main.css"
import React, { useEffect, useCallback, useRef } from "react";
import { GoSignOut, GoQuestion, GoKebabHorizontal, GoPlus, GoMail, GoTrash, GoArchive } from "react-icons/go";

import CreateCompModel from "./CreateCompModel";
import ArchiveCompModal from "./ArchiveCompModal";
import Game from "./Game";
import Results from "./Results";
import ErrorModal from "./errorModal";
import CompInfo from "./CompInfo";
import SEO from "./SEO";
import LoadingSpinner from "./LoadingSpinner";

import { getCompetitionsForUser, getCompetitionForUser, getStatusOfRollOver } from "../https_handler/apiHandler";
import Scoreboard from "./Scoreboard";
import CompDropdown from "./CompDropdown";
import InviteUserModal from "./InviteUserModal";

import { URL_HELPER, ROLL_OVER_STATUSES } from "../utils/constants";

import theDailyCompLogo from "../assets/Main_logo_proto1.svg";

import Joyride from "react-joyride";

const Main = ({ user, signOut }) => {
    const initialLoadRef = useRef(false);
    const userRef = useRef(user);
    const activeCompetitionRef = useRef(null);
    const reloadInProgressRef = useRef(false);
    
    const [guesses, setGuesses] = React.useState([]);
    const [competitions, setCompetitions] = React.useState([]);
    const [archivedCompetitions, setArchivedCompetitions] = React.useState([]);
    const [activeCompetition, setActiveCompetition] = React.useState(null);
    const [activeCompetitionLoading, setActiveCompetitionLoading] = React.useState(true);
    const [isCreateModelOpen, setIsCreateModelOpen] = React.useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = React.useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = React.useState();
    const [errorModelMessage, setErrorModelMessage] = React.useState("The error message");
    const [canUserInviteComp, setCanUserInviteComp] = React.useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
    const [dateToNextWord, setDateToNextWord] = React.useState();
    const [showScoreBoard, setShowScoreBoard] = React.useState(true);
    const [remainingGuesses, setRemainingGuesses] = React.useState();

    const [showTimerLoading, setShowTimerLoading] = React.useState(false);
    const [isMenuDropdownOpen, setIsMenuDropdownOpen] = React.useState(false);
    const [isPastCompsMenuOpen, setIsPastCompsMenuOpen] = React.useState(false);


    const [joyrideShouldRun, setJoyrideShouldRun] = React.useState(false);
    const [joyRideSteps] = React.useState([
        {
            target: ".view-competitions-button",
            content: "View all your competitions here, both those you create and those you are invited to can be found here!",
        },
        {
            target: ".create-competitions-button",
            content: "Create your own competition here and challenge your friends or co-workers!",
        },
        {
            target: ".sign-out-button",
            content: "All good things must come to an end... so once your done for the day sign out here.",
        },
        {
            target: ".game-play-container",
            content: "Everything for playing the game will be in here, information about your competition and the ability to make a guess will show up here.",
        },
        {
            target: ".letter-input-container",
            content: "Make an educated guess against a selected competition here. Simply type using your device's keyboard!",
        },
        {
            target: ".guess-button",
            content: "Submit your guess here or press Enter on your keyboard!",
        },
        {
            target: ".game-scoreboard-container",
            content: "View your score here and how you compare against your peers every month!",
        },
    ]);


    const handleClose = () => setIsCreateModelOpen(false);
    const handleShow = () => setIsCreateModelOpen(true);

    // Keep refs in sync with state/props
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    useEffect(() => {
        activeCompetitionRef.current = activeCompetition;
    }, [activeCompetition]);

    const handleInviteModalClose = () => setIsInviteModalOpen(false);
    const handleInviteModalShow = () => setIsInviteModalOpen(true);

    const handleDeleteModalClose = () => setIsDeleteModalOpen(false);

    const toggleMenuDropdown = () => setIsMenuDropdownOpen(!isMenuDropdownOpen);
    const closeMenuDropdown = () => {
        setIsMenuDropdownOpen(false);
        setIsPastCompsMenuOpen(false);
    };
    const togglePastCompsMenu = () => setIsPastCompsMenuOpen(!isPastCompsMenuOpen);

    const handlePastCompClick = async (competition, event) => {
        if (event) {
            event.stopPropagation();
        }
        try {
            const compId = competition.compId || competition.id;
            
            if (!compId) {
                console.error('No competition ID found:', competition);
                return;
            }
            
            // Update the URL safely
            try {
                const newUrl = `${window.location.origin}${URL_HELPER.URL_BASE_STRING}${URL_HELPER.URL_COMPETITION_PARAM}${compId}`;
                window.history.replaceState(null, '', newUrl);
            } catch (historyError) {
                console.warn('Could not update URL:', historyError);
                // Continue anyway - URL update is not critical
            }
            
            await changeActiveCompetition(compId);
            closeMenuDropdown();
            setIsPastCompsMenuOpen(false);
        } catch (error) {
            console.error('Error changing competition:', error);
        }
    };

    const getHref = (competition) => {
        const compId = competition.compId || competition.id;
        return `${URL_HELPER.URL_BASE_STRING}${URL_HELPER.URL_COMPETITION_PARAM}${compId}`;
    };


    const signUserOut = useCallback(() => {
        signOut();
    }, [signOut])

    const handleErrorModalClose = () => setIsErrorModalOpen(false);

    const handleErrorModalShow = (message) => {
        setErrorModelMessage(message);
        setIsErrorModalOpen("aValue");
    }

    const getActiveAndArchivedComps = useCallback((competitions) => {
        const archivedComps = [];
        const activeComps = [];

        for (const comp of competitions) {
            const { finished } = comp;
            if (finished) {
                archivedComps.push(comp)
                continue;
            }

            activeComps.push(comp);
        }

        // Sort archived competitions by endDate in descending order (most recent first)
        archivedComps.sort((a, b) => {
            const dateA = new Date(a.endDate);
            const dateB = new Date(b.endDate);
            return dateB.getTime() - dateA.getTime(); // Descending order
        });

        return { archivedComps, activeComps };
    }, [])

    const isCompOneMonthOld = (endDate) => {
        const dateObj = new Date(endDate);
        const currentDate = new Date(Date.now());
        currentDate.setMonth(currentDate.getMonth() - 1);

        if (dateObj < currentDate)
            return true;

        return false;
    }

    const handleArchiveComp = async () => {
        setIsDeleteModalOpen(true);
    }

    const setUrlToBase = () => {
        window.location.href = URL_HELPER.URL_BASE_STRING;
    }

    const reloadCompetitionScoreboard = async () => {
        setActiveCompetition({ ...activeCompetition, topUsers: [] });
        await getCompInfo(activeCompetition.compInfo.compId);
    }

    const getCompInfo = useCallback(async (compId) => {
        setActiveCompetitionLoading(true);
        const result = await getCompetitionForUser(userRef.current, compId);

        const { error } = result;
        if (error) {
            setActiveCompetitionLoading(false);
            handleErrorModalShow(error);
            // Don't auto-redirect - let users stay and see the error
            // setTimeout(setUrlToBase, 1500);
            return;
        }

        setRemainingGuesses(result.compInfo.maxGuesses - result.user.currentGuesses.length)
        setActiveCompetition(result);
        setCanUserInviteComp(result.compInfo.isUserOwner);
        setActiveCompetitionLoading(false);
    }, [])

    const reloadNewComp = useCallback(async () => {
        // Prevent concurrent reloads
        if (reloadInProgressRef.current) {
            return;
        }
        
        // Don't reload if viewing an archived competition (has results)
        if (activeCompetitionRef.current?.compInfo?.results) {
            return;
        }

        reloadInProgressRef.current = true;
        setShowTimerLoading(true);

        const { status, } = await getStatusOfRollOver(userRef.current);

        if (status === ROLL_OVER_STATUSES.running) {
            reloadInProgressRef.current = false;
            setTimeout(() => {
                reloadNewComp();
            }, 5000);

        } else if (status === ROLL_OVER_STATUSES.succeeded) {

            setShowTimerLoading(false);
            if (activeCompetitionRef.current?.compInfo?.compId) {
                await getCompInfo(activeCompetitionRef.current.compInfo.compId);
            }

            const currentDate = new Date(Date.now());
            currentDate.setUTCMinutes(2);
            currentDate.setUTCSeconds(0);
            currentDate.setUTCHours(12);
            currentDate.setUTCDate(currentDate.getUTCDate() + 1);

            setDateToNextWord(currentDate.toUTCString());
            setShowTimerLoading(false);
            reloadInProgressRef.current = false;
        } else {
            reloadInProgressRef.current = false;
        }
    }, [getCompInfo])

    const setDateToNextWordFunc = () => {
        const currentDate = new Date(Date.now());

        currentDate.setUTCMinutes(2);
        currentDate.setUTCSeconds(0);
        currentDate.setUTCHours(12);

        const actualCurrentDate = new Date(Date.now());
        actualCurrentDate.setUTCMinutes(actualCurrentDate.getUTCMinutes() - 15);

        if (actualCurrentDate.getTime() > currentDate.getTime()) {
            currentDate.setUTCDate(currentDate.getUTCDate() + 1);
        }

        setDateToNextWord(currentDate.toUTCString());
    }

    const changeActiveCompetition = async (compId) => {
        if (activeCompetition && activeCompetition.compInfo.compId === compId) return;

        await getCompInfo(compId);
    }

    const handleRunJoyRide = () => {
        setJoyrideShouldRun(true);
    }

    const getUsersCompetitions = useCallback(async () => {
        const { items, error, meta } = await getCompetitionsForUser(userRef.current);

        if (error) {
            handleErrorModalShow(error);
            setActiveCompetitionLoading(false);
            return;
        }

        if (meta.firstLogin)
            setJoyrideShouldRun(true);

        const { archivedComps, activeComps } = getActiveAndArchivedComps(items);

        setArchivedCompetitions(archivedComps);
        setCompetitions(activeComps);

        // Only auto-select the single active competition if:
        // 1. There's exactly one active competition
        // 2. No competition is currently loaded
        // 3. The URL doesn't specify a competition to load
        // Note: We check activeCompetition inside the function but don't include it in dependencies
        // to avoid re-running this function when competition changes
        const shouldAutoSelect = activeComps && activeComps.length === 1 && !window.location.href.includes(URL_HELPER.URL_COMPETITION_PARAM);
        if (shouldAutoSelect) {
            // Check if competition is already loaded by reading the current state
            setActiveCompetition(current => {
                if (!current) {
                    getCompInfo(activeComps[0].compId);
                }
                return current;
            });
        } else {
            // Multiple competitions or no competitions - stop loading to show "select competition" message
            setActiveCompetitionLoading(false);
        }
    }, [getActiveAndArchivedComps, getCompInfo])

    const setStateFromURL = useCallback(() => {
        if (window.location.href.includes(URL_HELPER.URL_COMPETITION_PARAM)) {
            const competition = window.location.href.split(URL_HELPER.URL_COMPETITION_PARAM)[1];

            getCompInfo(competition);
        }
        else {
        }
        //TODO else 
    }, [getCompInfo])

    // Initial load effect - runs once on mount
    useEffect(() => {
        if (initialLoadRef.current) return; // Prevent running more than once
        initialLoadRef.current = true;
        
        // Call functions directly to avoid dependency issues
        if (window.location.href.includes(URL_HELPER.URL_COMPETITION_PARAM)) {
            const competition = window.location.href.split(URL_HELPER.URL_COMPETITION_PARAM)[1];
            getCompInfo(competition);
        }
        
        getUsersCompetitions();
        setDateToNextWordFunc();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Separate effect for click outside handling
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMenuDropdownOpen && !event.target.closest('.header-menu-dropdown')) {
                setIsMenuDropdownOpen(false);
                setIsPastCompsMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuDropdownOpen]);

    // Generate dynamic SEO based on active competition
    const getSEOProps = () => {
        if (activeCompetition?.compInfo?.results) {
            // Archived competition with results
            const compName = activeCompetition.compInfo.compName || 'Competition';
            return {
                title: `${compName} - Results | The Daily Comp`,
                description: `Competition results for ${compName}. See who claimed the golden pencil and view the final leaderboard!`,
                url: `https://thedailycomp.com/?competition=${activeCompetition.compInfo.compId}`
            };
        } else if (activeCompetition?.compInfo?.compName) {
            // Active competition - only update SEO if we have the competition name
            return {
                title: `${activeCompetition.compInfo.compName} - Active Competition | The Daily Comp`,
                description: `Join ${activeCompetition.compInfo.compName} and compete with friends in today's word challenge. Can you claim the golden pencil?`,
                url: `https://thedailycomp.com/?competition=${activeCompetition.compInfo.compId}`
            };
        }
        // Default homepage (or loading state)
        return {};
    };

    return (
        <div className="Main">
            <SEO {...getSEOProps()} />
            
            {/* Modern Header */}
            <header className="header-container">
                <div className="header-main-row">
                    <div className="header-left">
                        <div className="header-menu-dropdown">
                            <button 
                                className="menu-button" 
                                onClick={toggleMenuDropdown}
                                title="Menu"
                            >
                                <GoKebabHorizontal size={20} />
                            </button>
                            
                            <div className={`menu-dropdown ${isMenuDropdownOpen ? 'open' : ''}`}>
                                <button 
                                    className="menu-item primary" 
                                    onClick={() => {
                                        closeMenuDropdown();
                                        handleShow();
                                    }}
                                >
                                    <GoPlus size={16} />
                                    Create Competition
                                </button>

                                {(archivedCompetitions.length > 0) && (
                                    <div className="menu-submenu-container">
                                        <button 
                                            className="menu-item" 
                                            onClick={togglePastCompsMenu}
                                        >
                                            <GoArchive size={16} />
                                            Past Competitions
                                            <span className={`submenu-arrow ${isPastCompsMenuOpen ? 'open' : ''}`}>▶</span>
                                        </button>
                                        
                                        {isPastCompsMenuOpen && (
                                            <div className="submenu">
                                                {archivedCompetitions.map((competition, index) => (
                                                    <button
                                                        key={`past_comp_${competition.compId || competition.id || index}`}
                                                        className="submenu-item"
                                                        onClick={(e) => handlePastCompClick(competition, e)}
                                                    >
                                                        <span className="submenu-title">{competition.title}</span>
                                                        <span className="submenu-status">
                                                            {new Date(competition.endDate).toLocaleDateString()}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {canUserInviteComp && (
                                    <>
                                        <button 
                                            className="menu-item" 
                                            onClick={() => {
                                                closeMenuDropdown();
                                                handleInviteModalShow();
                                            }}
                                        >
                                            <GoMail size={16} />
                                            Invite Users
                                        </button>
                                        <button 
                                            className="menu-item danger" 
                                            onClick={() => {
                                                closeMenuDropdown();
                                                handleArchiveComp();
                                            }}
                                        >
                                            <GoTrash size={16} />
                                            Delete Competition
                                        </button>
                                    </>
                                )}

                                <button 
                                    className="menu-item" 
                                    onClick={() => {
                                        closeMenuDropdown();
                                        handleRunJoyRide();
                                    }}
                                    title="Show Tutorial"
                                >
                                    <GoQuestion size={16} />
                                    Show Tutorial
                                </button>

                                <button 
                                    className="menu-item danger" 
                                    onClick={() => {
                                        closeMenuDropdown();
                                        signUserOut();
                                    }}
                                    title="Sign Out"
                                >
                                    <GoSignOut size={16} />
                                    Sign Out
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <div className="header-dropdowns">
                        <div className="view-competitions-button">
                            <CompDropdown
                                competitions={competitions}
                                setActiveCompetition={setActiveCompetition}
                                title={"Active Competitions"}
                                changeActiveCompetition={changeActiveCompetition}
                            />
                        </div>
                    </div>
                    
                    <div className="header-actions">
                        <img 
                            alt="The Daily Comp Logo" 
                            onClick={() => { window.location.href = "/" }} 
                            className="logo" 
                            src={theDailyCompLogo}
                        />
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="Container">
                <div className="game-area-container fade-in-up">
                    <div className="game-play-container">
                        {activeCompetitionLoading ? (
                            <LoadingSpinner message="Loading competition..." />
                        ) : activeCompetition !== null ? (
                            <>
                                <CompInfo
                                    activeCompetition={activeCompetition}
                                    reloadNewComp={reloadNewComp}
                                    guesses={guesses}
                                    remainingGuesses={remainingGuesses}
                                />
                                
                                {activeCompetition.compInfo.results ? (
                                    <Results activeCompetition={activeCompetition} />
                                ) : (
                                    <Game
                                        handleCorrectGuess={reloadCompetitionScoreboard}
                                        user={user}
                                        activeCompetition={activeCompetition}
                                        showErrorModal={handleErrorModalShow}
                                        dateToNextWord={dateToNextWord}
                                        reloadNewComp={reloadNewComp}
                                        showTimerLoading={showTimerLoading}
                                        setShowScoreBoard={setShowScoreBoard}
                                        remainingGuesses={remainingGuesses}
                                        setRemainingGuesses={setRemainingGuesses}
                                        guesses={guesses}
                                        setGuesses={setGuesses}
                                    />
                                )}
                            </>
                        ) : (
                            <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                padding: '4rem 2rem',
                                gap: '1rem',
                                textAlign: 'center'
                            }}>
                                <p style={{ 
                                    color: 'var(--gray-700)', 
                                    fontSize: '1.25rem',
                                    fontWeight: '600',
                                    margin: 0
                                }}>
                                    No active competition selected
                                </p>
                                <p style={{ 
                                    color: 'var(--gray-500)', 
                                    fontSize: '1rem',
                                    margin: 0
                                }}>
                                    Select a competition from the dropdown above or create a new one
                                </p>
                            </div>
                        )}
                    </div>

                    {showScoreBoard && activeCompetition && (
                        <div className="game-scoreboard-container">
                            <div className="game-scoreboard-header-container">
                                <h3 className="scoreboard-title">
                                    🏆 Leaderboard
                                </h3>
                            </div>
                            <Scoreboard
                                isLoading={activeCompetitionLoading}
                                activeCompetition={activeCompetition}
                            />
                        </div>
                    )}
                </div>
            </main>

            {/* Modals */}
            <CreateCompModel
                user={user}
                handleClose={handleClose}
                show={isCreateModelOpen}
                showErrorModal={handleErrorModalShow}
            />

            <InviteUserModal
                user={user}
                handleClose={handleInviteModalClose}
                show={isInviteModalOpen}
                activeCompetition={activeCompetition}
                showErrorModal={handleErrorModalShow}
            />

            <ArchiveCompModal
                user={user}
                handleClose={handleDeleteModalClose}
                show={isDeleteModalOpen}
                activeCompetition={activeCompetition}
                showErrorModal={handleErrorModalShow}
            />

            <ErrorModal
                handleClose={handleErrorModalClose}
                show={isErrorModalOpen}
                message={errorModelMessage}
            />

            <Joyride
                steps={joyRideSteps}
                scrollToFirstStep
                showProgress
                showSkipButton={false}
                hideBackButton
                hideCloseButton
                run={joyrideShouldRun}
                continuous
                disableCloseOnEsc={true}
                disableOverlayClose={true}
            />
        </div>
    )
}

export default Main;